/*
 * Copyright (c) 2022 CalAmp Corp.  All Rights Reserved
 */
import React, {
  ChangeEvent,
  useCallback,
  useEffect,
  useMemo,
  useState
} from 'react';
import isEmpty from 'lodash.isempty';
import {translate} from '@ion/core/src/locale/locale';
import {loadSCIDevicesBySerialNumber} from '@ion/core/src/services/Connect';
import {GenericObject} from '@ion/core/src/types/entities';
import {isValidESN} from '@ion/core/src/utils/DeviceUtils';
import {FormItem} from '@ion/dls/src/components/forms/FormItem';
import {FormSection} from '@ion/dls/src/components/forms/FormSection';
import Dropdown from '@ion/dls/src/components/menus/Dropdown';
import {IonSnackbar} from '@ion/dls/src/notifications/IonSnackbar';
import {BulkSCIUpload} from './BulkSCIUpload';
import {getAllSCIDevices, SCITypeNames} from './SCISettingsUtils';

export interface BulkSCIImportProps {
  classes: {
    [key: string]: string;
  };
  uploadValidation: boolean;
  setActiveStep: Function;
  setSelectedSCITypesDevices: Function;
  setUploadValidation: Function;
  setBulkUploadPersist: Function;
  bulkUploadPersist: GenericObject;
  isPersist: boolean;
  setIsPersist: Function;
  setSelectedDevicetype: Function;
  accountID?: number;
  setSelectedSCITypes: Function;
}

export interface GetOptions {
  value: string | boolean | any;
  label: string | any;
};

export type GetOptionsResult = GetOptions[];

/**
 * Display Bulk SCI Import Dialog
 */
export const BulkSCIImport = ({
  classes,
  uploadValidation,
  setActiveStep,
  isPersist,
  bulkUploadPersist,
  setUploadValidation,
  setBulkUploadPersist,
  setSelectedSCITypesDevices,
  setIsPersist,
  setSelectedDevicetype,
  accountID,
  setSelectedSCITypes
}: BulkSCIImportProps) => {
  const [deviceIds, setDeviceIds]               = useState([]);
  const [deviceTypes, setDeviceTypes]           = useState<GenericObject>({});
  const [errorDeviceTypes, setErrorDeviceTypes] = useState<GenericObject>({});
  const [showSnackBar, setShowSnackBar]         = useState<boolean>(false);

  const getOptions = (key: string): GetOptionsResult => {
    const entries = translate(`admin-devices:view.bulkSciSetting.${key}`, {
      returnObjects : true
    });
    const result = Object.entries(entries).map(([value, label]) => (
      {value, label}
    ));
    return result;
  };

  const idTypeOptions = useMemo((): GetOptionsResult => (
    getOptions('idTypeOptions')
  ), []);

  const typeOptions = useMemo((): GetOptionsResult => (
    getOptions('typeOptions')
  ), []);

  const getOptionIndex = useCallback((type: string, value: string): number => {
    const entries = translate(`admin-devices:view.bulkSciSetting.${type}`, {
      returnObjects : true
    });
    const idValue = bulkUploadPersist?.deviceTypes[value];
    return Object.keys(entries).indexOf(idValue);
  }, [isPersist]);

  useEffect(() => {
    if (uploadValidation) {
      const errorStatus = {
        ...errorDeviceTypes,
        deviceError    : !deviceTypes?.idType,
        typeError      : !deviceTypes?.deviceType,
        typeValueError : checkValidIdentifiers()
      };
      setErrorDeviceTypes(errorStatus);
      validateNextClick(errorStatus);
    }
  }, [uploadValidation, deviceTypes]);

  const checkValidIdentifiers = useCallback((): boolean => {
    let isValid = true;
    if (!isEmpty(deviceIds) || deviceTypes?.deviceIdValues) {
      const deviceIdentifiers = !isEmpty(deviceIds) ? deviceIds : getIdsByTextField();
      if (deviceIdentifiers.length) {
        deviceIdentifiers.forEach(element => {
          if (!isValidESN(element)) {
            isValid = false;
          }
        });
        return !isValid;
      }
    }
    return isValid;
  }, [deviceIds, deviceTypes]);

  useEffect(() => {
    if (isPersist) {
      setDeviceIds(bulkUploadPersist?.deviceIds);
      setDeviceTypes(bulkUploadPersist?.deviceTypes);
    }
  }, [isPersist]);

  const getIdsByTextField = useCallback((): string[] => {
    const ids = deviceTypes?.deviceIdValues.trim().split('\n');
    return ids;
  }, [deviceTypes]);

  const validateNextClick = useCallback((errorStatus: GenericObject) => {
    (async function() {
      const {deviceError, typeError, typeValueError} = errorStatus;
      if (!deviceError && !typeError && !typeValueError) {
        const deviceIdentifiers = !isEmpty(deviceIds) ? deviceIds : getIdsByTextField();
        const type = Object.keys(SCITypeNames).includes(deviceTypes?.deviceType)
          ? SCITypeNames[deviceTypes?.deviceType]
          : deviceTypes?.deviceType;
        try {
          const sciDevices1 = await loadSCIDevicesBySerialNumber(deviceIdentifiers, deviceTypes, accountID);
          if (sciDevices1.length) {
            const devices = sciDevices1.map(({device}) => device);
            const {selectedAllTypes, sciDevices} = getAllSCIDevices(devices);
            setSelectedSCITypesDevices(sciDevices);
            setSelectedDevicetype(deviceTypes?.deviceType);
            setActiveStep(1);
            setBulkUploadPersist({deviceIds, deviceTypes});
            setSelectedSCITypes(selectedAllTypes);
            setIsPersist(true);
          } else {
            setShowSnackBar(true);
          }
        } catch (err) {
          setShowSnackBar(true);
        }
      }
      setUploadValidation(false);
    })();
  }, [uploadValidation, deviceIds, deviceTypes, accountID]);

  const onTypeChange = useCallback((
    {target}: ChangeEvent<{name?: string, label?: string, value?: string}>
  ) => {
    const {name, value, label} = target;
    setDeviceTypes({
      ...deviceTypes,
      [name] : name === 'deviceIdValues' ? value : label
    });
    setUploadValidation(false);
  }, [deviceTypes]);

  function closeSnack() {
    setShowSnackBar(false);
    setUploadValidation(false);
  }

  return (
    <>
      <form className={classes.form}>
        <div className={classes.formWrapper}>
          <FormSection>
            <FormItem disableMinWidth>
              <Dropdown required
                name={'idType'}
                options={idTypeOptions}
                fieldLabel={translate('admin-devices:devicePassword.dialog.deviceIdentifierType')}
                errorText={translate('admin-devices:view.bulkSciSetting.typeErrorMessage')}
                hasError={errorDeviceTypes.deviceError}
                value={deviceTypes?.idType}
                onChange={onTypeChange}
                defaultValue={isPersist
                  ? idTypeOptions[getOptionIndex('idTypeOptions', 'idType')]
                  : idTypeOptions[0]}/>
            </FormItem>
            <FormItem>
              <Dropdown required
                name={'deviceType'}
                options={typeOptions}
                fieldLabel={translate('admin-devices:createJob.dialog.deviceType')}
                errorText={translate('admin-devices:view.bulkSciSetting.typeErrorMessage')}
                hasError={errorDeviceTypes.typeError}
                value={deviceTypes?.deviceType}
                onChange={onTypeChange}
                defaultValue={isPersist
                  ? typeOptions[getOptionIndex('typeOptions', 'deviceType')]
                  : deviceTypes?.deviceType}/>
            </FormItem>
            <BulkSCIUpload classes={classes}
              onTypeChange={onTypeChange}
              deviceTypes={deviceTypes}
              setDeviceTypes={setDeviceTypes}
              errorDeviceTypes={errorDeviceTypes}
              deviceIds={deviceIds}
              setDeviceIds={setDeviceIds}
              isPersist={isPersist}/>
          </FormSection>
        </div>
      </form>
      <IonSnackbar show={showSnackBar}
        message={translate('admin-devices:view.bulkSciSetting.bulkInfoMessages.noSCIDeviceMessage')}
        actionLabel={translate('common:editors.delete.ok')}
        onAction={closeSnack}
        onClose={closeSnack}
        autoHideDuration={3000} />
    </>
  );
};

export default BulkSCIImport;
